/**
 * useSandboxStore — Zustand store for sandbox lifecycle + credential management.
 *
 * Calls real gateway endpoints:
 *   GET  /api/sandbox/list           → list org sandboxes
 *   GET  /api/sandbox/:id/status     → get sandbox status
 *   POST /api/sandbox/create         → create sandbox
 *   POST /api/sandbox/:id/wake       → wake sleeping sandbox
 *   POST /api/sandbox/:id/hibernate  → hibernate (graceful stop)
 *   POST /api/sandbox/:id/destroy    → full teardown
 *   POST /api/sandbox/:id/trigger    → trigger opencode session
 *   POST /api/sandbox/:id/config     → push config update
 *   GET  /api/sandbox/credentials    → list credential providers
 *   POST /api/sandbox/credentials    → save credential
 *   DELETE /api/sandbox/credentials/:provider → delete credential
 */
import { create } from "zustand";
import { api } from "@/lib/api";
import type { SandboxLifecycleState } from "@ajnt/shared";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

export interface SandboxInfo {
  id: string;
  orgId: string;
  state: SandboxLifecycleState;
  containerId?: string;
  instanceType?: string;
  endpointUrl?: string;
  lastActiveAt?: string;
  uptimeSeconds?: number;
  config?: Record<string, unknown>;
  createdAt?: string;
}

export interface CredentialEntry {
  provider: string;
  createdAt?: string;
}

export interface SandboxEvent {
  type: string;
  orgId?: string;
  timestamp?: number;
  payload?: Record<string, unknown>;
}

/* ------------------------------------------------------------------ */
/*  State & actions                                                    */
/* ------------------------------------------------------------------ */

interface SandboxState {
  /** The primary sandbox for this org (most orgs have one) */
  sandbox: SandboxInfo | null;
  /** All sandboxes for the org */
  sandboxes: SandboxInfo[];
  /** Configured credential providers */
  credentials: CredentialEntry[];
  /** Loading indicator */
  loading: boolean;
  /** Last error message */
  error: string | null;
}

interface SandboxActions {
  /* Sandbox lifecycle */
  fetchSandboxes(): Promise<void>;
  fetchSandboxStatus(sandboxId: string): Promise<void>;
  launchSandbox(): Promise<void>;
  wakeSandbox(sandboxId: string): Promise<void>;
  stopSandbox(sandboxId: string): Promise<void>;
  destroySandbox(sandboxId: string): Promise<void>;
  triggerSession(sandboxId: string, prompt: string, agent?: string): Promise<void>;
  pushConfig(sandboxId: string, patch: Record<string, unknown>): Promise<void>;

  /* Credentials */
  fetchCredentials(): Promise<void>;
  saveCredential(provider: string, apiKey: string): Promise<void>;
  deleteCredential(provider: string): Promise<void>;

  /* WebSocket event handler */
  handleSandboxEvent(event: SandboxEvent): void;

  /* Utility */
  clearError(): void;
}

export const useSandboxStore = create<SandboxState & SandboxActions>(
  (set, get) => ({
    sandbox: null,
    sandboxes: [],
    credentials: [],
    loading: false,
    error: null,

    /* ------------------------------------------------------------ */
    /*  Sandbox lifecycle                                            */
    /* ------------------------------------------------------------ */

    async fetchSandboxes() {
      set({ loading: true, error: null });
      try {
        const result = await api.get<{ sandboxes: SandboxInfo[] }>(
          "/api/sandbox/list",
        );
        const sandboxes = Array.isArray(result?.sandboxes) ? result.sandboxes : [];

        // Pick the best sandbox by state priority
        const statePriority: Record<string, number> = {
          active: 0,
          waking: 1,
          creating: 2,
          provisioning: 3,
          sleeping: 4,
          error: 5,
          terminated: 6,
        };

        const sorted = [...sandboxes].sort((a, b) => {
          const pa = statePriority[a.state] ?? 99;
          const pb = statePriority[b.state] ?? 99;
          return pa - pb;
        });

        // Pick first non-terminated sandbox, or null
        const sandbox = sorted.find(s => s.state !== "terminated") ?? null;

        set({ sandboxes, sandbox, loading: false });
      } catch (err) {
        set({
          loading: false,
          error: err instanceof Error ? err.message : "Failed to load sandboxes",
        });
      }
    },

    async fetchSandboxStatus(sandboxId: string) {
      try {
        const status = await api.get<SandboxInfo>(
          `/api/sandbox/${sandboxId}/status`,
        );
        if (!status) return;
        set((state) => {
          const sandboxes = state.sandboxes.map((sb) =>
            sb.id === sandboxId ? { ...sb, ...status } : sb,
          );
          const sandbox =
            state.sandbox?.id === sandboxId
              ? { ...state.sandbox, ...status }
              : state.sandbox;
          return { sandboxes, sandbox };
        });
      } catch (err) {
        set({
          error:
            err instanceof Error ? err.message : "Failed to fetch sandbox status",
        });
      }
    },

    async launchSandbox() {
      set({ loading: true, error: null });
      try {
        const result = await api.post<{ sandboxId: string; state: string }>(
          "/api/sandbox/create",
        );
        if (result?.sandboxId) {
          const newSandbox: SandboxInfo = {
            id: result.sandboxId,
            orgId: "",
            state: (result.state as SandboxLifecycleState) ?? "creating",
          };
          set((state) => ({
            sandboxes: [...state.sandboxes, newSandbox],
            sandbox: newSandbox,
            loading: false,
          }));
        } else {
          set({ loading: false });
        }
      } catch (err) {
        set({
          loading: false,
          error:
            err instanceof Error ? err.message : "Failed to launch sandbox",
        });
      }
    },

    async wakeSandbox(sandboxId: string) {
      set({ loading: true, error: null });
      try {
        await api.post(`/api/sandbox/${sandboxId}/wake`);
        set((state) => {
          const update = (sb: SandboxInfo) =>
            sb.id === sandboxId ? { ...sb, state: "waking" as SandboxLifecycleState } : sb;
          return {
            sandboxes: state.sandboxes.map(update),
            sandbox:
              state.sandbox?.id === sandboxId
                ? { ...state.sandbox, state: "waking" as SandboxLifecycleState }
                : state.sandbox,
            loading: false,
          };
        });
      } catch (err) {
        set({
          loading: false,
          error: err instanceof Error ? err.message : "Failed to wake sandbox",
        });
      }
    },

    async stopSandbox(sandboxId: string) {
      set({ loading: true, error: null });
      try {
        await api.post(`/api/sandbox/${sandboxId}/hibernate`);
        set((state) => {
          const update = (sb: SandboxInfo) =>
            sb.id === sandboxId ? { ...sb, state: "sleeping" as SandboxLifecycleState } : sb;
          return {
            sandboxes: state.sandboxes.map(update),
            sandbox:
              state.sandbox?.id === sandboxId
                ? { ...state.sandbox, state: "sleeping" as SandboxLifecycleState }
                : state.sandbox,
            loading: false,
          };
        });
      } catch (err) {
        set({
          loading: false,
          error: err instanceof Error ? err.message : "Failed to stop sandbox",
        });
      }
    },

    async destroySandbox(sandboxId: string) {
      set({ loading: true, error: null });
      try {
        await api.post(`/api/sandbox/${sandboxId}/destroy`);
        set((state) => ({
          sandboxes: state.sandboxes.filter((sb) => sb.id !== sandboxId),
          sandbox:
            state.sandbox?.id === sandboxId ? null : state.sandbox,
          loading: false,
        }));
      } catch (err) {
        set({
          loading: false,
          error:
            err instanceof Error ? err.message : "Failed to destroy sandbox",
        });
      }
    },

    async triggerSession(sandboxId: string, prompt: string, agent?: string) {
      set({ error: null });
      try {
        await api.post(`/api/sandbox/${sandboxId}/trigger`, {
          action: "start_session" as const,
          message: prompt,
          agent,
        });
      } catch (err) {
        set({
          error:
            err instanceof Error ? err.message : "Failed to trigger session",
        });
      }
    },

    async pushConfig(sandboxId: string, patch: Record<string, unknown>) {
      set({ error: null });
      try {
        await api.post(`/api/sandbox/${sandboxId}/config`, patch);
        // Merge config into local state
        set((state) => {
          if (state.sandbox?.id === sandboxId) {
            return {
              sandbox: {
                ...state.sandbox,
                config: { ...(state.sandbox.config ?? {}), ...patch },
              },
            };
          }
          return {};
        });
      } catch (err) {
        set({
          error:
            err instanceof Error ? err.message : "Failed to push config",
        });
      }
    },

    /* ------------------------------------------------------------ */
    /*  Credentials                                                  */
    /* ------------------------------------------------------------ */

    async fetchCredentials() {
      try {
        const result = await api.get<{ providers: CredentialEntry[] }>(
          "/api/sandbox/credentials",
        );
        const credentials = Array.isArray(result?.providers)
          ? result.providers
          : [];
        set({ credentials });
      } catch (err) {
        set({
          error:
            err instanceof Error
              ? err.message
              : "Failed to load credentials",
        });
      }
    },

    async saveCredential(provider: string, apiKey: string) {
      set({ error: null });
      try {
        await api.post("/api/sandbox/credentials", { provider, apiKey });
        // Optimistically add to local list (if not already present)
        set((state) => {
          const exists = state.credentials.some((c) => c.provider === provider);
          if (exists) return {};
          return {
            credentials: [
              ...state.credentials,
              { provider, createdAt: new Date().toISOString() },
            ],
          };
        });
      } catch (err) {
        set({
          error:
            err instanceof Error
              ? err.message
              : "Failed to save credential",
        });
        throw err; // re-throw so caller can handle
      }
    },

    async deleteCredential(provider: string) {
      set({ error: null });
      try {
        await api.delete(`/api/sandbox/credentials/${provider}`);
        set((state) => ({
          credentials: state.credentials.filter(
            (c) => c.provider !== provider,
          ),
        }));
      } catch (err) {
        set({
          error:
            err instanceof Error
              ? err.message
              : "Failed to delete credential",
        });
        throw err;
      }
    },

    /* ------------------------------------------------------------ */
    /*  WebSocket event handler                                      */
    /* ------------------------------------------------------------ */

    handleSandboxEvent(event: SandboxEvent) {
      const { type, payload } = event;
      const sandboxId = (payload?.sandboxId as string) ?? get().sandbox?.id;
      if (!sandboxId) return;

      const stateFromType = (t: string): SandboxLifecycleState | null => {
        switch (t) {
          case "sandbox.state_changed":
            return (payload?.state as SandboxLifecycleState) ?? null;
          case "sandbox.ready":
            return "active";
          case "sandbox.error":
            return "error";
          case "sandbox.sleeping":
            return "sleeping";
          case "sandbox.waking":
            return "waking";
          default:
            return null;
        }
      };

      const newState = stateFromType(type);
      if (!newState) return;

      set((state) => {
        const update = (sb: SandboxInfo) =>
          sb.id === sandboxId ? { ...sb, state: newState } : sb;
        return {
          sandboxes: state.sandboxes.map(update),
          sandbox:
            state.sandbox?.id === sandboxId
              ? { ...state.sandbox, state: newState }
              : state.sandbox,
          // Clear error if recovery
          error: newState === "error" ? (payload?.message as string ?? "Sandbox error") : state.error,
        };
      });
    },

    /* ------------------------------------------------------------ */
    /*  Utility                                                      */
    /* ------------------------------------------------------------ */

    clearError() {
      set({ error: null });
    },
  }),
);
